import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { aggregatePermitDates } from "./commercialPermitsSchema";
import { isCvinExpansion } from "../projectGridColumns";
import { parseFootageNumber, workOrderBaseForRow } from "./expansionGroupUtils";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import { formatReportFootage, timeframeBounds } from "./expansionBudgetReportData";

function parsePermitDate(dateValue) {
  if (dateValue == null || String(dateValue).trim() === "") return null;
  const raw = String(dateValue).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function matchesPermitTimeframe(permitReceivedDate, bounds) {
  if (!bounds.from && !bounds.to) return true;
  const d = parsePermitDate(permitReceivedDate);
  if (!d) return false;
  if (bounds.from && d < bounds.from) return false;
  if (bounds.to && d > bounds.to) return false;
  return true;
}

const PIE_COLORS = {
  caltrans: "#2563eb",
  nonCaltrans: "#16a34a",
};

function readEngineeringFromAcf(acfGroupsJson) {
  const j = acfGroupsJson && typeof acfGroupsJson === "object" ? acfGroupsJson : {};
  return (
    j[COMMERCIAL_DOMAINS.ENGINEERING] ??
    j.engineering_details ??
    j["Engineering Details"] ??
    {}
  );
}

function parseMonthKey(dateValue) {
  if (dateValue == null || String(dateValue).trim() === "") return null;
  const raw = String(dateValue).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function formatPermittedMonthLabel(monthKey) {
  if (!monthKey) return "—";
  const [y, m] = monthKey.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  if (Number.isNaN(d.getTime())) return monthKey;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/** Projects with "Caltrans" in the project name are treated as Caltrans permits. */
export function isCaltransPermitProject(projectName) {
  return /caltrans/i.test(String(projectName ?? "").trim());
}

function projectTitleForRow(row, subSummary) {
  return (
    subSummary?.project_name ??
    row?.name ??
    row?.site_name ??
    row?.customer ??
    ""
  );
}

function permittedFootageForSubsegment(subSummary, isCaltrans) {
  const caltrans = parseFootageNumber(subSummary?.caltrans_footage);
  const construction = parseFootageNumber(subSummary?.construction_footage);
  const override = parseFootageNumber(subSummary?.override_footage);
  const primary =
    override != null && override > 0
      ? override
      : construction != null && construction > 0
        ? construction
        : null;

  if (isCaltrans) {
    if (caltrans != null && caltrans > 0) return caltrans;
    return primary;
  }

  if (primary == null) return null;
  const ct = caltrans != null && caltrans > 0 ? caltrans : 0;
  return Math.max(0, primary - ct);
}

/**
 * One row per expansion subsegment that has countable footage under the permitted-footage
 * rules. Includes both permitted and not-yet-permitted (remaining) rows.
 */
export function buildExpansionPermitEligibleDetailRows(projects) {
  const expansionRows = (Array.isArray(projects) ? projects : []).filter(isCvinExpansion);
  const rows = [];

  for (const row of expansionRows) {
    const subSummary = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
    const eng = readEngineeringFromAcf(row?.acf_groups_json);
    const permitReceivedDate = aggregatePermitDates(eng?.permits, {
      includeSupportingDocuments: false,
    }).permit_received_date;
    const monthKey = parseMonthKey(permitReceivedDate);

    const projectName = projectTitleForRow(row, subSummary);
    const isCaltrans = isCaltransPermitProject(projectName);
    const footage = permittedFootageForSubsegment(subSummary, isCaltrans);
    if (footage == null || footage <= 0) continue;

    const isPermitted = Boolean(monthKey);
    rows.push({
      id: String(row.id ?? `${row.work_order}-${subSummary?.segment ?? ""}`),
      workOrder: subSummary?.work_order ?? row?.work_order ?? "",
      woBase: workOrderBaseForRow(row),
      segment: subSummary?.segment ?? "",
      projectName: String(projectName).trim(),
      permitReceivedDate: isPermitted ? String(permitReceivedDate).trim() : "",
      monthKey: monthKey || "",
      phase: subSummary?.phase ?? row?.phase_label ?? "",
      category: isCaltrans ? "Caltrans" : "Non-Caltrans",
      isCaltrans,
      isPermitted,
      permittedFootage: isPermitted ? Math.round(footage * 100) / 100 : 0,
      remainingFootage: isPermitted ? 0 : Math.round(footage * 100) / 100,
      footage: Math.round(footage * 100) / 100,
      caltransFootage: parseFootageNumber(subSummary?.caltrans_footage),
      constructionFootage: parseFootageNumber(subSummary?.construction_footage),
      overrideFootage: parseFootageNumber(subSummary?.override_footage),
    });
  }

  return rows.sort((a, b) => {
    if (a.isPermitted !== b.isPermitted) return a.isPermitted ? -1 : 1;
    if (a.monthKey !== b.monthKey) return String(b.monthKey).localeCompare(String(a.monthKey));
    return String(a.workOrder).localeCompare(String(b.workOrder), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
}

/** One row per permitted expansion subsegment (has permit_received_date). */
export function buildExpansionPermittedDetailRows(projects) {
  return buildExpansionPermitEligibleDetailRows(projects).filter((row) => row.isPermitted);
}

export function aggregatePermittedByMonth(detailRows) {
  const byMonth = new Map();

  for (const row of detailRows) {
    const bucket = byMonth.get(row.monthKey) || {
      monthKey: row.monthKey,
      caltransFootage: 0,
      nonCaltransFootage: 0,
      segmentCount: 0,
    };
    if (row.isCaltrans) {
      bucket.caltransFootage += row.permittedFootage;
    } else {
      bucket.nonCaltransFootage += row.permittedFootage;
    }
    bucket.segmentCount += 1;
    byMonth.set(row.monthKey, bucket);
  }

  return [...byMonth.values()]
    .map((bucket) => {
      const caltransFootage = Math.round(bucket.caltransFootage * 100) / 100;
      const nonCaltransFootage = Math.round(bucket.nonCaltransFootage * 100) / 100;
      return {
        ...bucket,
        caltransFootage,
        nonCaltransFootage,
        totalFootage: Math.round((caltransFootage + nonCaltransFootage) * 100) / 100,
        monthLabel: formatPermittedMonthLabel(bucket.monthKey),
      };
    })
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
}

export function listPermittedMonthKeys(monthlyRows) {
  return (Array.isArray(monthlyRows) ? monthlyRows : []).map((row) => row.monthKey);
}

export function aggregatePermittedPieForMonth(detailRows, monthKey) {
  let caltrans = 0;
  let nonCaltrans = 0;

  for (const row of detailRows) {
    if (monthKey && row.monthKey !== monthKey) continue;
    if (row.isCaltrans) caltrans += row.permittedFootage;
    else nonCaltrans += row.permittedFootage;
  }

  caltrans = Math.round(caltrans * 100) / 100;
  nonCaltrans = Math.round(nonCaltrans * 100) / 100;
  const total = caltrans + nonCaltrans;

  const slices = [
    { name: "Caltrans", value: caltrans, color: PIE_COLORS.caltrans },
    { name: "Non-Caltrans", value: nonCaltrans, color: PIE_COLORS.nonCaltrans },
  ].filter((slice) => slice.value > 0);

  return slices.map((slice) => ({
    ...slice,
    pct: total > 0 ? slice.value / total : 0,
    faded: false,
  }));
}

export function filterPermittedDetailRows(detailRows, filters = {}) {
  const {
    phases = [],
    timeframe = "all",
    customFrom = "",
    customTo = "",
  } = filters;
  const bounds = timeframeBounds(timeframe, customFrom, customTo);
  const phaseSet = new Set((phases || []).map((p) => String(p).trim()).filter(Boolean));

  return (Array.isArray(detailRows) ? detailRows : []).filter((row) => {
    if (phaseSet.size > 0 && !phaseSet.has(row.phase)) return false;
    return matchesPermitTimeframe(row.permitReceivedDate, bounds);
  });
}

export function summarizePermittedDetail(detailRows) {
  let caltransFootage = 0;
  let nonCaltransFootage = 0;

  for (const row of detailRows) {
    if (row.isCaltrans) caltransFootage += row.permittedFootage;
    else nonCaltransFootage += row.permittedFootage;
  }

  caltransFootage = Math.round(caltransFootage * 100) / 100;
  nonCaltransFootage = Math.round(nonCaltransFootage * 100) / 100;

  return {
    segmentCount: detailRows.length,
    caltransFootage,
    nonCaltransFootage,
    totalFootage: Math.round((caltransFootage + nonCaltransFootage) * 100) / 100,
  };
}

/** Remaining = eligible footage that does not yet have a primary permit received date. */
export function summarizeRemainingPermittedDetail(eligibleRows) {
  let remainingFootage = 0;
  let remainingCount = 0;
  let caltransRemaining = 0;
  let nonCaltransRemaining = 0;
  let eligibleFootage = 0;

  for (const row of Array.isArray(eligibleRows) ? eligibleRows : []) {
    const ft = Number(row.footage) || 0;
    eligibleFootage += ft;
    if (row.isPermitted) continue;
    remainingCount += 1;
    remainingFootage += ft;
    if (row.isCaltrans) caltransRemaining += ft;
    else nonCaltransRemaining += ft;
  }

  return {
    remainingCount,
    remainingFootage: Math.round(remainingFootage * 100) / 100,
    caltransRemaining: Math.round(caltransRemaining * 100) / 100,
    nonCaltransRemaining: Math.round(nonCaltransRemaining * 100) / 100,
    eligibleFootage: Math.round(eligibleFootage * 100) / 100,
  };
}

export function aggregatePermittedPieChart(detailRows) {
  return aggregatePermittedPieForMonth(detailRows, "");
}

export function toPermittedMonthlyGridRows(monthlyRows) {
  return (Array.isArray(monthlyRows) ? monthlyRows : []).map((row) => ({
    id: row.monthKey,
    monthKey: row.monthKey,
    monthLabel: row.monthLabel,
    caltransFootage: row.caltransFootage,
    nonCaltransFootage: row.nonCaltransFootage,
    totalFootage: row.totalFootage,
    segmentCount: row.segmentCount,
  }));
}

export { formatReportFootage };
